// @flow

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import Button from '../../games/PictureSearch/NewButton';
import StampBoard from './NewStampBoard';
import StampItem from './NewStampItem';
import useGameState, { STATES } from '../../games/utils/use_game_state';
import useWindowSize from '../../games/utils/use_window_size';
import Game from '../../games/Shared/Game';

import type { AudioAssets } from './types';

type Props = {
  images: Array<string>,
  backgroundImage: string,
  assetPath: string,
  audio: AudioAssets,
  title: string,
  back: string,
  isApp: Boolean,
};

const Stamp = (props: Props) => {
  const newImage = (imgSrc) => {
    const img = new Image();
    img.src = imgSrc;
    return img;
  };

  const [cursorImg, setCursorImg] = useState(newImage(props.images[0]));
  const [showBoard, setShowBoard] = useState(false);

  const [gameState] = useGameState();

  const [page, setPage] = useState(0);

  const [stampsPerPage, setStampsPerPage] = useState(3);
  const windowSize = useWindowSize();

  useEffect(() => {
    if (windowSize.width > windowSize.height && windowSize.width <= 812) {
      setStampsPerPage(2);
    } else {
      setStampsPerPage(3);
    }
  }, [windowSize]);

  useEffect(() => {
    const introAudio = document.createElement('audio');
    introAudio.src = props.audio.intro;
    introAudio.play();
  }, [props.audio.intro]);

  useEffect(() => {
    if (gameState >= STATES.PLAY && !showBoard) {
      setTimeout(() => {
        setShowBoard(true);
      }, 300);
    }
  }, [gameState, showBoard]);

  const clipArray = (num: number): number => _.clamp(num, 0, props.images.length);
  const pages = Math.ceil(props.images.length / stampsPerPage);

  const switchStamp = (e: SyntheticEvent<HTMLDivElement | HTMLImageElement>): void => {
    const stampDiv = e.target instanceof Image ? e.target.parentElement : e.target;
    if (stampDiv instanceof HTMLDivElement && stampDiv.firstChild instanceof HTMLImageElement) {
      setCursorImg(newImage(stampDiv.firstChild.src));
    }
  };

  const prevPage = () => {
    if (page <= 0) {
      return pages - 1;
    }
    return page - 1;
  };

  const nextPage = () => {
    if (page >= pages - 1) {
      return 0;
    }
    return page + 1;
  };

  const renderStamps = (): Array<React$Element<any>> => (
    props.images.slice(
      clipArray(page * stampsPerPage),
      clipArray((page * stampsPerPage) + stampsPerPage),
    ).map(
      (image, index) => (<StampItem
        img={image}
        key={`stamp-${index}`}
        stampClass="bg-contain bg-no-repeat bg-center"
        click={switchStamp}
      />),
    )
  );

  return (
    <Game gameState={gameState} relatedGames={[]} assetPath={props.assetPath} isApp={props.isApp}>
      { gameState >= STATES.PLAY &&
        <div className={`p-1 ps__base w-full max-w-8xl flex portrait:flex-col landscape:flex-row items-center h-full portrait:space-y-4 landscape:space-x-4 mx-auto transition-opacity duration-300 ease-in ${showBoard ? 'opacity-100' : 'opacity-0'}`}>
          <div className="portrait:w-full landscape:w-5/6">
            <StampBoard
              assetPath={props.assetPath}
              cursorImg={cursorImg}
              background={props.backgroundImage}
              againAudio={props.audio.again}
            />
          </div>
          <div className="relative max-h-full max-w-full flex portrait:flex-row landscape:flex-col space-2 bg-white rounded-lg items-center mx-auto shadow portrait:px-2 landscape:py-2">
            {props.images.length > stampsPerPage && <Button
              img={`${props.assetPath}assets/games/picture_search/chevron-left.png`}
              buttonClass="transform portrait:rotate-0 landscape:rotate-90 portrait:-ml-2 landscape:-mt-2"
              click={() => setPage(prevPage())}
              showArrow={true}
            />}
            {
              renderStamps()
            }
            {props.images.length > stampsPerPage && <Button
              img={`${props.assetPath}assets/games/picture_search/chevron-right.png`}
              buttonClass="transform portrait:rotate-0 landscape:rotate-90 portrait:-mr-2 landscape:-mb-2"
              click={() => setPage(nextPage())}
              showArrow={true}
            />}
          </div>
        </div>
      }
    </Game>
  );
};

const stampElement = document.querySelector('#stamp');

if (stampElement) {
  const data = stampElement.dataset;
  const props = {
    back: data.back,
    title: data.title,
    audio: JSON.parse(data.audio),
    images: JSON.parse(data.images),
    backgroundImage: data.backgroundImage,
    assetPath: data.assetPath,
    isApp: data.app === 'true',
  };
  ReactDOM.render(<Stamp {...props} />, stampElement);
}
